import React, { useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import Card from "components/card";
import AccessButton from "components/button/accessbutton";
import { CATEGORY_ICONS, validateImageAttachment } from "variables/constants"
import { Paperclip, Send, X, FileText } from "lucide-react";
import { Form, message } from "antd";
import FormItem from "components/fields/FormItem";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";
import Button from "components/button";
import TicketReplyCard from "./TicketReplyCard";
import AttachmentCard from "components/card/AttachmentCard";
import {
    doTicketCreate,
    getTicketDetails
} from "../../../../../redux/ticket/ticketState";
import { API_URL } from "services/apiservice";

const TicketDetailCard = ({
    recordData: initialTicket,
    userId,
    doTicketCreate,
    getTicketDetails,
    variant = 'it-support'
}) => {
    const [form] = Form.useForm();
    const [markAsResolved, setMarkAsResolved] = useState(false);
    const [attachment, setAttachment] = useState(null); 
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const repliesEndRef = useRef(null);

    const ticketDetails = useSelector(state => state.ticket.ticketDetails?.data);
    const recordData = (ticketDetails && ticketDetails.tic_id === initialTicket?.tic_id) 
        ? ticketDetails 
        : initialTicket;

    const [localReplies, setLocalReplies] = useState(recordData?.replies || []);

    const config = {
        'it-support': {
            baseRoute: '/console/it-support',
            gradientFrom: 'from-blue-500', gradientTo: 'to-cyan-500',
            gradientLightFrom: 'from-blue-50', gradientLightTo: 'to-cyan-50',
            gradientDarkFrom: 'dark:from-blue-900', gradientDarkTo: 'dark:to-cyan-900',
            borderColor: 'border-blue-500', darkBorderColor: 'dark:border-blue-700',
            buttonGradientFrom: 'from-blue-600', buttonGradientTo: 'to-cyan-600',
            buttonHoverFrom: 'hover:from-blue-700', buttonHoverTo: 'hover:to-cyan-700',
        },
        'hr-support': {
            baseRoute: '/console/hr-support',
            gradientFrom: 'from-pink-500', gradientTo: 'to-rose-500',
            gradientLightFrom: 'from-pink-50', gradientLightTo: 'to-rose-50',
            gradientDarkFrom: 'dark:from-pink-900', gradientDarkTo: 'dark:to-rose-900',
            borderColor: 'border-pink-500', darkBorderColor: 'dark:border-pink-700',
            buttonGradientFrom: 'from-pink-600', buttonGradientTo: 'to-rose-600',
            buttonHoverFrom: 'hover:from-pink-700', buttonHoverTo: 'hover:to-rose-700',
        },
        'partner-service': {
            baseRoute: '/console/partner-service',
            gradientFrom: 'from-purple-500', gradientTo: 'to-indigo-500',
            gradientLightFrom: 'from-purple-50', gradientLightTo: 'to-indigo-50',
            gradientDarkFrom: 'dark:from-purple-900', gradientDarkTo: 'dark:to-indigo-900',
            borderColor: 'border-purple-500', darkBorderColor: 'dark:border-purple-700',
            buttonGradientFrom: 'from-purple-600', buttonGradientTo: 'to-indigo-600',
            buttonHoverFrom: 'hover:from-purple-700', buttonHoverTo: 'hover:to-indigo-700',
        }
    };

    const currentConfig = config[variant] || config['it-support'];

    useEffect(() => {
        if (initialTicket?.tic_id) {
            getTicketDetails({ tic_id: initialTicket.tic_id });
        }
    }, []);

    useEffect(() => {
        if (ticketDetails?.replies && ticketDetails.tic_id === initialTicket?.tic_id) {
            setLocalReplies(ticketDetails.replies);
        }
    }, [ticketDetails?.replies]);

    const scrollToBottom = () => {
        setTimeout(() => {
            repliesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    useEffect(() => {
        scrollToBottom();
    }, [localReplies]);

    const handleAttachClick = (e) => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e) => {
        e.preventDefault();
        const file = e.target.files[0];
        
        if (!file) {
            return;
        }

        const result = await validateImageAttachment(file, { maxSizeMB: 20, maxWidth: 8000, maxHeight: 8000 });
        
        if (!result.valid) {
            message.error(result.error);
            return;
        }
        setAttachment(file);
    };

    const removeAttachment = () => setAttachment(null);

    const Icon = CATEGORY_ICONS[recordData?.ticket_category];
    const isDepartmentMember = userId !== recordData?.created_by?.id;
    const isResolved = recordData?.ticket_status === 'resolved';

    const onSubmit = (formData) => {
        if (isSubmitting) return;
        if (!formData.tic_message?.trim()) {
            message.warning('Please enter a message');
            return;
        }

        setIsSubmitting(true);

        setLocalReplies([...localReplies, {
            tic_id: Date.now(),
            tic_parent_id: recordData.tic_id,
            tic_user_id: userId,
            tic_message: formData.tic_message,
            tic_is_internal: isDepartmentMember ? 0 : 1,
            created_at: new Date().toISOString(),
            created_date: new Date().toISOString().split('T')[0],
            created_time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            user_name: 'You',
        }]);

        const payload = new FormData();
        payload.append('tic_parent_id', recordData.tic_id);
        payload.append('tic_user_id', userId);
        payload.append('tic_message', formData.tic_message);
        payload.append('tic_is_internal', isDepartmentMember ? 0 : 1);
        payload.append('tic_is_seen', 0);
        payload.append('mark_as_resolved', markAsResolved ? 1 : 0);
        if (attachment) payload.append('attachment', attachment);

        doTicketCreate(payload);

        setTimeout(() => getTicketDetails({ tic_id: recordData.tic_id }), 500);

        form.resetFields();
        setAttachment(null);
        setMarkAsResolved(false);
        scrollToBottom();
        message.success('Reply sent successfully');
        setIsSubmitting(false);
    };

    const sortedReplies = [...localReplies].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    const canReply = () => {
        if (isResolved) return false;
        if (isDepartmentMember) return true;
        return localReplies.length > 0;
    };

    return (
        <div className="space-y-6 max-w-[1400px] mx-auto">
            <AccessButton 
                backLabel="Back to Tickets" 
                withBack={true} 
                baseRoute={currentConfig.baseRoute} 
            />
            <Card className={`bg-white muted-text flex flex-col gap-6 rounded-xl border-2 ${currentConfig.borderColor} shadow-xl overflow-hidden dark-mode-card ${currentConfig.darkBorderColor}`}>
                <div className={`grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 grid-cols-[1fr_auto] [.border-b]:pb-6 bg-gradient-to-br ${currentConfig.gradientLightFrom} ${currentConfig.gradientLightTo} border-b ${currentConfig.gradientDarkFrom} ${currentConfig.gradientDarkTo} dark:border-none`}>
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                            <div className={`p-3 rounded-xl bg-gradient-to-br ${currentConfig.gradientFrom} ${currentConfig.gradientTo} text-white shadow-md`}>
                                {Icon && <Icon className="w-6 h-6" />}
                            </div>
                            <div>
                                <h4 className="text-[20px] mb-1 title-text">{recordData?.tic_title}</h4>
                                <p className="muted-text text-[13px]">Ticket ID: {recordData?.tic_code} • Submitted by {recordData?.submitted_by}</p>
                                <div className="flex items-center gap-2 mt-2">
                                    <span className={`status-indicator uppercase font-semibold ${recordData?.ticket_priority?.replace(/\s+/g, "-") || ''}`}>{recordData?.ticket_priority}</span>
                                    <span className={`status-indicator uppercase font-semibold ${recordData?.ticket_status?.replace(/\s+/g, "-") || ''}`}>{recordData?.ticket_status}</span>
                                    <span className={`status-indicator text-[10px] ${recordData?.ticket_category?.replace(/\s+/g, "-") || ''}`}>{recordData?.ticket_category}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="pb-6 p-6">
                    <div className="space-y-6">
                        <div>
                            <h4 className="font-semibold text-[14px] mb-2 title-text">Description</h4>
                            <p className="text-[13px] muted-text">{recordData?.tic_description}</p>
                        </div>
                        {recordData?.attachment > 0 && (
                            <div className="space-y-2 sm:space-y-2.5">
                                <div>
                                    <h4 className="font-semibold text-[12px] sm:text-[13px] text-slate-700 mb-2 flex items-center gap-2">
                                        <FileText className="w-3 h-3 " />
                                        Attachment
                                    </h4>
                                    <AttachmentCard
                                        attachment={{
                                            id: recordData?.attachment?.att_id,
                                            name: recordData?.attachment?.att_filename,
                                        }}
                                        onDownload={(file) => {
                                            window.open(
                                                `${API_URL}/download/attachment/${file.id}`,
                                                "_blank"
                                            );
                                        }}
                                        onView={(file) => {
                                            window.open(
                                                `${API_URL}/preview/attachment/${file.id}`,
                                                "_blank"
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="bg-slate-200 shrink-0 h-px w-full"></div>
                        <div>
                            <h4 className="font-semibold text-[14px] mb-3">Conversation ({sortedReplies.length})</h4>
                            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                                {sortedReplies.map((reply, index) => (
                                    <TicketReplyCard 
                                        key={reply.tic_id || index} 
                                        reply={reply} 
                                        department={recordData?.department} 
                                        isCurrentUser={reply.tic_user_id === userId} 
                                        isCustomer={reply.tic_user_id === recordData?.created_by?.id} 
                                    />
                                ))}
                                <div ref={repliesEndRef} />
                            </div>
                            {isResolved && <div className="text-center py-4 mt-4"><p className="text-[12px] text-gray-600">This ticket has been resolved.</p></div>}
                            {canReply() && (
                                <div className="mt-4">
                                    <Form form={form} layout="vertical" onFinish={onSubmit}>
                                        <FormItem 
                                            name="tic_message" 
                                            rules={[requiredField()]}
                                        >
                                            <TextArea 
                                                id="tic_message" 
                                                className="input-field" 
                                                maxLength={1000} 
                                                placeholder="Type your response..." 
                                                rows={4} 
                                            />
                                        </FormItem>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <input 
                                                    type="file" 
                                                    ref={fileInputRef} 
                                                    onChange={handleFileSelect} 
                                                    style={{ display: 'none' }} 
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={handleAttachClick}
                                                    disabled={isSubmitting}
                                                    className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all shrink-0 border bg-white text-slate-900 h-8 rounded-lg px-3 gap-2"
                                                >
                                                    <Paperclip className="w-4 h-4" /> 
                                                    Attach File
                                                </Button>
                                                {attachment && (
                                                    <div className="flex items-center gap-2 text-[11px] p-1.5 rounded-md text-slate-600 bg-slate-50">
                                                        {attachment.name}
                                                        <button type="button" 
                                                            onClick={removeAttachment} 
                                                            className="text-red-400 hover:text-red-600"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                )}
                                                {isDepartmentMember && !isResolved && (
                                                    <div className="flex items-center gap-2 ml-4">
                                                        <input type="checkbox" id="markAsResolved" checked={markAsResolved} onChange={(e) => setMarkAsResolved(e.target.checked)} className="w-4 h-4 rounded-lg border-gray-300" />
                                                        <label htmlFor="markAsResolved" className="text-[11px] text-slate-700 cursor-pointer">Mark as resolved</label>
                                                    </div>
                                                )}
                                            </div>
                                            <Button 
                                                htmlType="submit" 
                                                disabled={isSubmitting} 
                                                loading={isSubmitting} 
                                                className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-all shrink-0 h-9 px-4 py-2 gap-2 bg-gradient-to-r ${currentConfig.buttonGradientFrom} ${currentConfig.buttonGradientTo} text-white`}><Send className="w-4 h-4" 
                                            /> 
                                                Send Reply
                                            </Button>
                                        </div>
                                    </Form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({
    userId: state.auth?.user?.id,
});

const mapDispatchToProps = (dispatch) => ({
    doTicketCreate: (data) => dispatch(doTicketCreate(data)),
    getTicketDetails: (data) => dispatch(getTicketDetails(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TicketDetailCard);